import React, { useState, useCallback } from 'react'
import { Upload, FileSpreadsheet, Check, AlertCircle, Loader2, X, ArrowRight } from 'lucide-react'
import { toast } from 'sonner'

const API_URL = process.env.REACT_APP_BACKEND_URL

export default function ImportIAModal({ isOpen, onClose, tenantId, policeId, importType, onSuccess }) {
  const [step, setStep] = useState('upload') // upload | preview | importing | done
  const [file, setFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [result, setResult] = useState(null)

  const typeLabel = importType === 'vehicules' ? 'vehicules' : 'sites'

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0]
    if (f) setFile(f)
  }, [])

  async function handleAnalyze() {
    if (!file) return
    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('import_type', importType)
      formData.append('tenant_id', tenantId)
      if (policeId) formData.append('police_id', policeId)

      const res = await fetch(`${API_URL}/api/import-ia/preview`, { method: 'POST', body: formData })
      if (!res.ok) throw new Error('Erreur analyse')
      const data = await res.json()

      if (data.error) {
        toast.error(data.error)
        return
      }
      setPreview(data)
      setStep('preview')
    } catch (err) {
      toast.error('Erreur lors de l\'analyse du fichier')
    } finally {
      setLoading(false)
    }
  }

  async function handleImport() {
    if (!preview || !file) return
    setStep('importing')
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('import_type', importType)
      formData.append('tenant_id', tenantId)
      formData.append('mapping', JSON.stringify(preview.mapping))
      if (policeId) formData.append('police_id', policeId)

      const res = await fetch(`${API_URL}/api/import-ia/execute-file`, { method: 'POST', body: formData })
      if (res.ok) {
        const data = await res.json()
        setResult(data)
        setStep('done')
        toast.success(`Import termine: ${data.created || 0} crees, ${data.updated || 0} mis a jour`)
        onSuccess?.()
      } else {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || 'Erreur import')
      }
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'import')
      setStep('preview')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="import-ia-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Import intelligent — {typeLabel}</h2>
            <p className="text-xs text-slate-400">L'IA analyse votre fichier et mappe automatiquement les colonnes</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${
                  file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-indigo-50/30'
                }`}
              >
                {file ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="mx-auto text-emerald-600" size={40} />
                    <p className="text-sm font-semibold text-emerald-700">{file.name}</p>
                    <p className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} Ko</p>
                    <button onClick={() => setFile(null)} className="text-xs text-red-500 hover:text-red-700">Changer de fichier</button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="mx-auto text-slate-400" size={40} />
                    <p className="text-sm text-slate-600">Glissez votre fichier Excel ou CSV ici</p>
                    <p className="text-xs text-slate-400">ou cliquez pour parcourir</p>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0])} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </div>
                )}
              </div>
              <div className="relative">
                <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0])} className="hidden" id="file-input" />
                {!file && (
                  <label htmlFor="file-input" className="block text-center text-sm text-indigo-600 hover:text-indigo-700 cursor-pointer font-medium">
                    Parcourir les fichiers
                  </label>
                )}
              </div>
              {file && (
                <button
                  onClick={handleAnalyze}
                  disabled={loading}
                  className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="btn-analyze"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  {loading ? 'Analyse en cours...' : 'Analyser avec l\'IA'}
                </button>
              )}
            </div>
          )}

          {/* Step 2: Preview mapping */}
          {step === 'preview' && preview && (
            <div className="space-y-5">
              {/* Mapping summary */}
              <div className="bg-indigo-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-indigo-800 mb-2">Mapping des colonnes detecte par l'IA</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(preview.mapping).map(([target, source]) => (
                    <div key={target} className="flex items-center gap-2 text-xs">
                      <span className={`font-semibold ${source ? 'text-emerald-700' : 'text-slate-400'}`}>{target}</span>
                      <ArrowRight size={10} className="text-slate-300" />
                      <span className={source ? 'text-slate-700' : 'text-red-400 italic'}>
                        {source || 'Non trouve'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-4">
                <div className="flex-1 bg-slate-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-800">{preview.total_rows}</p>
                  <p className="text-xs text-slate-500">lignes dans le fichier</p>
                </div>
                <div className="flex-1 bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{preview.mapped_rows}</p>
                  <p className="text-xs text-slate-500">lignes mappees</p>
                </div>
              </div>

              {/* Preview table */}
              {preview.preview?.length > 0 && (
                <div className="overflow-x-auto">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Apercu des donnees (20 premieres lignes)</p>
                  <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
                    <thead className="bg-slate-100">
                      <tr>
                        {Object.keys(preview.preview[0]).map(k => (
                          <th key={k} className="px-3 py-2 text-left font-semibold text-slate-600">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.slice(0, 10).map((row, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          {Object.values(row).map((v, j) => (
                            <td key={j} className="px-3 py-1.5 text-slate-700">{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <button onClick={() => { setStep('upload'); setPreview(null) }} className="flex-1 py-3 border border-slate-300 rounded-xl text-slate-600 hover:bg-slate-50 font-medium text-sm">
                  Retour
                </button>
                <button
                  onClick={handleImport}
                  className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 flex items-center justify-center gap-2"
                  data-testid="btn-import"
                >
                  <Check size={16} />
                  Importer {preview.mapped_rows} {typeLabel}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="text-center py-12">
              <Loader2 size={40} className="mx-auto animate-spin text-indigo-600 mb-4" />
              <p className="text-sm font-semibold text-slate-700">Import en cours...</p>
              <p className="text-xs text-slate-400 mt-1">Mise a jour du parc</p>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 'done' && result && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <Check size={30} className="text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-slate-900">Import termine</p>
                <div className="flex justify-center gap-6 mt-3">
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{result.created || 0}</p>
                    <p className="text-xs text-slate-500">crees</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{result.updated || 0}</p>
                    <p className="text-xs text-slate-500">mis a jour</p>
                  </div>
                  {result.sortis > 0 && (
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{result.sortis}</p>
                      <p className="text-xs text-slate-500">sortis du parc</p>
                    </div>
                  )}
                  {result.fermes > 0 && (
                    <div>
                      <p className="text-2xl font-bold text-amber-600">{result.fermes}</p>
                      <p className="text-xs text-slate-500">sites fermes</p>
                    </div>
                  )}
                </div>
                {result.errors?.length > 0 && (
                  <div className="mt-3 text-left bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    {result.errors.slice(0, 5).map((e, i) => (
                      <p key={i} className="text-xs text-red-600">{e}</p>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={onClose} className="px-6 py-2 bg-slate-800 text-white rounded-lg font-medium text-sm hover:bg-slate-900">
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
